import { Collapsible } from "@base-ui/react/collapsible";
import clsx from "clsx";

import type { FragmentType } from "../../../../../__generated__/gql";
import { graphql, useFragment } from "../../../../../__generated__/gql";
import { pagePath } from "../../../../../utils";
import { useInNativeApp } from "../../../../_hooks/use-in-native-app";
import { postMessageToNativeApp } from "../../../../utils/native-app";
import { Icons } from "../../../icons";
import { Link } from "../../../ui/link";

import type { AccommodationCount } from "./_utils/area-filtering";

const Fragment = graphql(`
  fragment AreaHeaderFragment on Area {
    id
    slug
    name
    content {
      allCapsName
      reservationFormCoverImageUrl
    }
  }
`);

export function AreaHeader({
  areaData,
  accommodationCount,
  dimmed,
}: {
  areaData: FragmentType<typeof Fragment>;
  accommodationCount?: AccommodationCount | null;
  dimmed?: boolean;
}) {
  const area = useFragment(Fragment, areaData);
  const { name, content } = area;
  const { allCapsName, reservationFormCoverImageUrl } = content;
  const isInNativeApp = useInNativeApp();
  return (
    <>
      <Collapsible.Trigger className="group relative flex w-full cursor-pointer items-center justify-between overflow-hidden bg-gray-800 px-[18px] py-[20px] text-white">
        {reservationFormCoverImageUrl && (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={reservationFormCoverImageUrl}
            alt=""
            className={clsx(
              "pointer-events-none absolute inset-0 h-full w-full object-cover",
              dimmed && "brightness-50",
            )}
          />
        )}
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, rgba(37, 39, 42, 0.90) 0%, rgba(37, 39, 42, 0.30) 23.96%, rgba(37, 39, 42, 0.00) 49.48%)",
          }}
        />
        <div className="relative flex flex-col text-left">
          <Link
            data-analytics-id="reservation_calendar_clicked_area_detail"
            href={pagePath(area)}
            target="_blank"
            onClick={(e) => {
              // Collapsible.Trigger の開閉トグルを阻止
              e.stopPropagation();
              // nativeapp の WebView 内では target="_blank" が機能しないため、
              // postMessage でネイティブに委譲し WebView 画面を modal で開く。
              if (isInNativeApp) {
                e.preventDefault();
                postMessageToNativeApp(
                  {
                    type: "OpenInWebView",
                    payload: {
                      url: `${window.location.origin}/areas/${area.slug}`,
                      presentation: "modal",
                      title: "エリア情報",
                    },
                  },
                  window,
                );
              }
            }}
            className="mb-[2px] text-[18px] underline underline-offset-[3px] hover:opacity-80"
          >
            {name}
          </Link>
          <div className="font-roboto text-[9px]">{allCapsName}</div>
        </div>
        <div className="relative flex items-center gap-[8px]">
          {accommodationCount && (
            <div className="text-skogecha-100 rounded-[4px] bg-white px-[4px] py-[4px] text-[10px] leading-none font-bold">
              {accommodationCount.available != null
                ? `${accommodationCount.available}/${accommodationCount.total}部屋`
                : `${accommodationCount.total}部屋`}
            </div>
          )}
          <Icons.ChevronDownIcon className="size-[18px] transition-transform duration-300 group-data-[panel-open]:rotate-180" />
        </div>
      </Collapsible.Trigger>
      <div className="border-oldsblack h-[3px] border-t-2 bg-white" />
    </>
  );
}
